/**
 * System Longevity Configuration
 *
 * Expected lifespans, replacement cycles, and reasoning for different heating systems.
 * Based on industry data, thermal cycling patterns, and component stress profiles.
 */

export interface LongevityConfig {
  expectedLifespanYears: { min: number; max: number };
  reasoning: string;
}

/**
 * Longevity profiles by system type and water heating method
 *
 * Key factors affecting lifespan:
 * - Thermal cycling frequency (on/off cycles cause stress)
 * - Heat exchanger design (compact vs. traditional)
 * - Component complexity (more parts = more failure modes)
 * - Operating temperatures (higher temps = more stress)
 * - Mechanical load (compressor duty, pump cycling)
 */
export const LONGEVITY_PROFILES: Record<string, LongevityConfig> = {
  // ==================== COMBI BOILERS ====================
  'combi': {
    expectedLifespanYears: { min: 10, max: 12 },
    reasoning: 'High thermal cycling due to on-demand DHW, compact heat exchanger design, frequent stop-start operation'
  },
  'storage_combi': {
    expectedLifespanYears: { min: 11, max: 13 },
    reasoning: 'Reduced cycling vs standard combi due to buffer storage, but still dual-mode operation with compact HX'
  },

  // ==================== SYSTEM BOILERS ====================
  'system': {
    expectedLifespanYears: { min: 15, max: 20 },
    reasoning: 'Lower cycling vs combi (cylinder stores DHW), simpler hydraulics, less on-demand stress'
  },

  // ==================== REGULAR BOILERS ====================
  'regular': {
    expectedLifespanYears: { min: 15, max: 25 },
    reasoning: 'Traditional design, low pressure system, minimal cycling, simple heat exchanger, proven longevity'
  },

  // ==================== HEAT PUMPS ====================
  'air_source_hp': {
    expectedLifespanYears: { min: 18, max: 22 },
    reasoning: 'Lower mechanical stress vs combustion, modular components, variable speed operation reduces cycling'
  },
  'ground_source_hp': {
    expectedLifespanYears: { min: 22, max: 25 },
    reasoning: 'Stable source temperature reduces thermal stress, lowest cycling frequency, ground loop lasts 50+ years'
  },
  'hybrid': {
    expectedLifespanYears: { min: 14, max: 18 },
    reasoning: 'Heat pump component longevity + boiler cycling wear, complexity adds failure modes but load-sharing helps'
  },

  // ==================== EXISTING/UNKNOWN SYSTEMS ====================
  'unknown': {
    expectedLifespanYears: { min: 8, max: 12 },
    reasoning: 'Unknown condition, age, and maintenance history; conservative estimate for aged equipment'
  },
  'existing': {
    expectedLifespanYears: { min: 8, max: 12 },
    reasoning: 'Already aged system, unknown remaining life, likely approaching end of design lifespan'
  },
};

/**
 * Calculate longevity metrics for a system over an analysis period
 */
export interface LongevityMetrics {
  expectedLifespanYears: { min: number; max: number };
  replacementsInPeriod: number;
  reasoning: string;
}

/**
 * Calculate replacements within analysis period (not at boundary)
 * 
 * Rules:
 * - Count replacements that occur strictly inside the analysis window, not at the end boundary
 * - A "20-year lifespan" system installed today should show 0 replacements in a 20-year analysis
 * - For new installs: floor((periodYears - ε) / lifespanYears) ensures period==lifespan → 0
 * - For baseline with remainingLife: accounts for initial remaining life, then subsequent replacements
 * 
 * @param periodYears - Analysis period in years
 * @param lifespanYears - Expected lifespan of the system in years
 * @param remainingLifeYears - Optional: remaining life of existing system
 * @returns Number of replacements that occur within the period (not at boundary)
 */
export function replacementsWithinPeriod(args: {
  periodYears: number;
  lifespanYears: number;
  remainingLifeYears?: number;
}): number {
  const { periodYears, lifespanYears, remainingLifeYears } = args;
  
  // Small epsilon to handle boundary cases (1ms ~ 3.17e-8 years)
  // Using 1e-6 for safety margin against floating-point precision issues
  const EPSILON = 1e-6;
  
  // New install (no remainingLife specified)
  if (remainingLifeYears === undefined) {
    // Use small epsilon to ensure boundary replacements don't count
    // E.g., 20-year period / 20-year lifespan = 0 replacements (not 1)
    return Math.floor((periodYears - EPSILON) / lifespanYears);
  }
  
  // Existing system with remaining life
  // If remaining life covers the entire period, no replacements needed
  if (remainingLifeYears >= periodYears) {
    return 0;
  }
  
  // First replacement will happen, then check for subsequent replacements
  const remainingWindow = periodYears - remainingLifeYears;
  // Count subsequent replacements (again using epsilon to avoid boundary count)
  return 1 + Math.floor((remainingWindow - EPSILON) / lifespanYears);
}

export function calculateLongevity(
  systemType: string,
  boilerType: string,
  analysisPeriodYears: number,
  systemAge?: number
): LongevityMetrics {
  // Determine which profile to use
  let profile: LongevityConfig;

  // Try exact system type first (e.g., 'air_source_hp')
  if (LONGEVITY_PROFILES[systemType]) {
    profile = LONGEVITY_PROFILES[systemType];
  }
  // Fall back to boiler type (e.g., 'combi', 'system', 'regular')
  else if (LONGEVITY_PROFILES[boilerType]) {
    profile = LONGEVITY_PROFILES[boilerType];
  }
  // Default to unknown
  else {
    profile = LONGEVITY_PROFILES['unknown'];
  }

  // Calculate replacements based on average lifespan
  const avgLifespan = (profile.expectedLifespanYears.min + profile.expectedLifespanYears.max) / 2;

  // If we know the system age, factor that into replacement calculation
  let effectiveLifeRemaining: number | undefined = undefined;
  if (systemAge !== undefined && systemAge > 0) {
    effectiveLifeRemaining = Math.max(avgLifespan - systemAge, 0);
  }

  // Use the canonical helper to calculate replacements
  const replacementsInPeriod = replacementsWithinPeriod({
    periodYears: analysisPeriodYears,
    lifespanYears: avgLifespan,
    remainingLifeYears: effectiveLifeRemaining,
  });

  return {
    expectedLifespanYears: profile.expectedLifespanYears,
    replacementsInPeriod,
    reasoning: profile.reasoning,
  };
}

/**
 * Calculate longevity for the existing/baseline system
 */
export function calculateBaselineLongevity(
  currentBoilerType: string,
  systemAge: number | undefined,
  analysisPeriodYears: number
): LongevityMetrics {
  // Use existing system profile
  const profile = LONGEVITY_PROFILES['existing'];

  // If we know the age, we can be more precise
  const avgLifespan = (profile.expectedLifespanYears.min + profile.expectedLifespanYears.max) / 2;
  const effectiveAge = systemAge || avgLifespan * 0.7; // Assume 70% through life if age unknown

  const remainingLife = Math.max(avgLifespan - effectiveAge, 2); // At least 2 years remaining

  // Use the canonical helper to calculate replacements
  const totalReplacements = replacementsWithinPeriod({
    periodYears: analysisPeriodYears,
    lifespanYears: avgLifespan,
    remainingLifeYears: remainingLife,
  });

  let reasoning = profile.reasoning;
  if (systemAge !== undefined) {
    reasoning = `System is ${systemAge} years old; ${profile.reasoning.toLowerCase()}`;
  }

  return {
    expectedLifespanYears: profile.expectedLifespanYears,
    replacementsInPeriod: totalReplacements,
    reasoning,
  };
}
